/*
 * Helper functions for checking for permissions.
 */
import {
  categoryToGlobalIssueResourceType,
  CategoryToGlobalProjectIssueResourceType,
  CategoryToGlobalReleaseGroupIssueResourceType,
  categoryToLocalIssueResourceType,
  CategoryToLocalProjectIssueResourceType,
  CategoryToLocalReleaseGroupIssueResourceType,
} from '../../shared/issuesV2/permissions';
import { IssueResolutionScope, PolicyType } from '../../typings/enums';
import { Action, ResourceType } from '../../typings/permissions';
import { window } from '../globals';
import type { Category } from '../../shared/issuesV2';

export const userHasPermissionForProject = (action: Action, projectId?: string) =>
  window.hasPermission({ resourceType: ResourceType.ProjectAny, action }) ||
  window.hasPermission({ resourceType: ResourceType.Project, action, projectId });

export const userHasPermissionToViewAnyProject = () =>
  window.hasPermission({ resourceType: ResourceType.ProjectAny, action: Action.View });

export const userHasPermissionToViewAnyReleaseGroup = () =>
  window.hasPermission({ resourceType: ResourceType.ReleaseGroupAny, action: Action.View });

export const userHasPermissionToEditAnyReleaseGroup = () =>
  window.hasPermission({ resourceType: ResourceType.ReleaseGroupAny, action: Action.Edit });

export const userHasPermissionToCreateReleaseGroups = () =>
  window.hasPermission({ resourceType: ResourceType.ReleaseGroup, action: Action.Create });

export const userHasPermissionToSetProjectPolicy = (projectId: string, policyType: PolicyType): boolean => {
  let globalResourceType: ResourceType;
  let localResourceType: ResourceType;
  if (policyType === PolicyType.SECURITY) {
    globalResourceType = ResourceType.ProjectSecurityPolicyAny;
    localResourceType = ResourceType.ProjectSecurityPolicy;
  } else if (policyType === PolicyType.QUALITY) {
    globalResourceType = ResourceType.ProjectQualityPolicyAny;
    localResourceType = ResourceType.ProjectQualityPolicy;
  } else if (policyType === PolicyType.SBOM) {
    globalResourceType = ResourceType.ProjectSBOMPolicyAny;
    localResourceType = ResourceType.ProjectSBOMPolicy;
  } else {
    globalResourceType = ResourceType.ProjectLicensingPolicyAny;
    localResourceType = ResourceType.ProjectLicensingPolicy;
  }

  return window.hasPermission({ resourceType: globalResourceType, action: Action.Set })
    || window.hasPermission({ resourceType: globalResourceType, action: Action.Edit })
    || window.hasPermission({ resourceType: localResourceType, action: Action.Set, projectId })
    || window.hasPermission({ resourceType: localResourceType, action: Action.Edit, projectId });
};

export const userHasPermissionToSetProjectFilters = (projectId: string, policyType: PolicyType): boolean => {
  let globalResourceType: ResourceType;
  let localResourceType: ResourceType;
  if (policyType === PolicyType.SECURITY) {
    globalResourceType = ResourceType.ProjectIssueFilterSecurityAny;
    localResourceType = ResourceType.ProjectIssueFilterSecurityAny;
  } else if (policyType === PolicyType.QUALITY) {
    globalResourceType = ResourceType.ProjectIssueFilterQualityAny;
    localResourceType = ResourceType.ProjectIssueFilterQuality;
  } else {
    globalResourceType = ResourceType.ProjectIssueFilterLicensingAny;
    localResourceType = ResourceType.ProjectIssueFilterLicensing;
  }

  return window.hasPermission({ resourceType: globalResourceType, action: Action.Edit })
    || window.hasPermission({ resourceType: localResourceType, action: Action.Edit, projectId });
};

export const userHasPermissionToCreateProjectLicensingReportLink = (projectId: string) =>
  window.hasPermission({
    resourceType: ResourceType.ProjectLicensingReportPublicLinkAny,
    action: Action.Create,
  }) ||
  window.hasPermission({
    resourceType: ResourceType.ProjectLicensingReportPublicLink,
    action: Action.Create,
    projectId,
  });

export const userHasPermissionToCreateTeamReports = (): boolean =>
  // window.hasPermission cannot be used for the TeamReport permission because it looks for the teamId
  // attribute which is not necessarily present for this permission.
  !!window.user?.permissions &&
    window.user.permissions.some(
      (permission) => permission.resourceType === ResourceType.TeamReport && permission.action === Action.Create,
    );

export const userHasPermissionToCreateOrgReports = (): boolean =>
  window.hasPermission({
    resourceType: ResourceType.OrganizationReport,
    action: Action.Create,
  });

export const userHasPermissionForReleaseGroup = (action: Action, groupId?: number) =>
  window.hasPermission({ resourceType: ResourceType.ReleaseGroupAny, action }) ||
  window.hasPermission({ resourceType: ResourceType.ReleaseGroup, action, releaseGroupId: groupId });

export const userHasPermissionToSetReleaseGroupPolicy = (releaseGroupId: number, policyType: PolicyType): boolean => {
  let globalResourceType: ResourceType;
  let localResourceType: ResourceType;
  if (policyType === PolicyType.SECURITY) {
    globalResourceType = ResourceType.ReleaseGroupSecurityPolicyAny;
    localResourceType = ResourceType.ReleaseGroupSecurityPolicy;
  } else if (policyType === PolicyType.QUALITY) {
    globalResourceType = ResourceType.ReleaseGroupQualityPolicyAny;
    localResourceType = ResourceType.ReleaseGroupQualityPolicy;
  } else {
    globalResourceType = ResourceType.ReleaseGroupLicensingPolicyAny;
    localResourceType = ResourceType.ReleaseGroupLicensingPolicy;
  }

  return window.hasPermission({ resourceType: globalResourceType, action: Action.Set }) ||
    window.hasPermission({ resourceType: localResourceType, action: Action.Set, releaseGroupId });
};

// userHasPermissionForProjectIssues returns whether the current user may perform an action on a project's issues.
export const userHasPermissionForProjectIssues = (
  projectId: string,
  issueCategory: Category,
  action: Action,
): boolean =>
  window.hasPermission({
    resourceType: CategoryToGlobalProjectIssueResourceType[issueCategory],
    action,
  }) ||
  window.hasPermission({
    resourceType: CategoryToLocalProjectIssueResourceType[issueCategory],
    action,
    projectId,
  });

// userHasPermissionForReleaseGroupIssues returns whether the current user may perform an action on the issues of
// a release group.
export const userHasPermissionForReleaseGroupIssues = (
  releaseGroupId: number,
  issueCategory: Category,
  action: Action,
): boolean =>
  window.hasPermission({
    resourceType: CategoryToGlobalReleaseGroupIssueResourceType[issueCategory],
    action,
  }) ||
  window.hasPermission({
    resourceType: CategoryToLocalReleaseGroupIssueResourceType[issueCategory],
    action,
    releaseGroupId,
  });

// IssueResolutionOptions defines the possible ways you could resolve an issue.
type IssueResolutionOptions =
| { scope: IssueResolutionScope.Global }
| { scope: IssueResolutionScope.Project; projectId: string }
| { scope: IssueResolutionScope.ReleaseGroup; releaseGroupId: number };

/**
 * Check whether or not the user has permission to resolve issues for a project or RG by a issue category instead of type.
 */
export const userHasPermissionToResolveIssueByCategory = (
  category: Category,
  options: IssueResolutionOptions,
): boolean =>
  window.hasPermission({
    resourceType: categoryToGlobalIssueResourceType(category, options.scope),
    action: Action.Resolve,
  }) ||
  window.hasPermission({
    resourceType: categoryToLocalIssueResourceType(category, options.scope),
    action: Action.Resolve,
    projectId: options.scope === IssueResolutionScope.Project ? options.projectId : null,
    releaseGroupId: options.scope === IssueResolutionScope.ReleaseGroup ? options.releaseGroupId : null,
  });

/**
 * Checks if user has permission to resolve issues of a specific category for *all* Release Groups.
 */
export const userHasPermissionToResolveAnyRg = (issueCategory: Category) =>
  window.hasPermission({
    resourceType: CategoryToGlobalReleaseGroupIssueResourceType[issueCategory],
    action: Action.Resolve,
  });

/**
 * Checks is user has permission to resolve issues of a specific category for all projects AND all release groups.
 */
export const userHasPermissionToResolveIssueAnyProjectAndRg = (issueCategory: Category) =>
  userHasPermissionToResolveAnyRg(issueCategory) &&
  window.hasPermission({
    resourceType: CategoryToGlobalProjectIssueResourceType[issueCategory],
    action: Action.Resolve,
  });

export const userHasPermissionForTeam = (action: Action, teamId: number) =>
  window.hasPermission({ resourceType: ResourceType.TeamAny, action }) ||
  window.hasPermission({ resourceType: ResourceType.Team, action, teamId });

export const userHasPermissionToEditTeamUsers = (teamId: number): boolean =>
  window.hasPermission({ resourceType: ResourceType.TeamUserAny, action: Action.Edit }) ||
  window.hasPermission({ resourceType: ResourceType.TeamUser, action: Action.Edit, teamId });

export const userHasPermissionToEditAnyTeamUsers = (): boolean =>
  window.hasPermission({ resourceType: ResourceType.TeamUserAny, action: Action.Edit });

export const userHasPermissionForTeamProjects = (action: Action, teamId: number) =>
  window.hasPermission({ resourceType: ResourceType.TeamProjectAny, action }) ||
  window.hasPermission({ resourceType: ResourceType.TeamProject, action, teamId });

export const userHasPermissionForTeamReleaseGroups = (action: Action, teamId: number) =>
  window.hasPermission({ resourceType: ResourceType.TeamReleaseGroupAny, action }) ||
  window.hasPermission({ resourceType: ResourceType.TeamReleaseGroup, action, teamId });

export const userHasPermissionForPolicyType = (action: Action, policyType: PolicyType) => {
  let resourceType: ResourceType;
  switch (policyType) {
    case PolicyType.SECURITY:
      resourceType = ResourceType.SecurityPolicyAny;
      break;
    case PolicyType.QUALITY:
      resourceType = ResourceType.QualityPolicyAny;
      break;
    case PolicyType.SBOM:
      resourceType = ResourceType.SBOMPolicyAny;
      break;
    default:
      resourceType = ResourceType.LicensingPolicyAny;
  }
  return window.hasPermission({ resourceType, action });
};

export const userHasPermissionToCreatePolicyType = (policyType: PolicyType) => {
  let resourceType: ResourceType;
  switch (policyType) {
    case PolicyType.SECURITY:
      resourceType = ResourceType.SecurityPolicy;
      break;
    case PolicyType.QUALITY:
      resourceType = ResourceType.QualityPolicy;
      break;
    case PolicyType.SBOM:
      resourceType = ResourceType.SBOMPolicy;
      break;
    default:
      resourceType = ResourceType.LicensingPolicy;
  }
  return window.hasPermission({ resourceType, action: Action.Create });
};
